import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import {
  CreateDeploymentGroupDto,
  UpdateDeploymentGroupDto,
  AssignPipelinesToGroupDto
} from '../types/pipelineFlow';

// Query Keys
export const queryKeys = {
  deploymentGroups: ['deploymentGroups'] as const,
  deploymentGroup: (id: string) => ['deploymentGroups', id] as const,
  pipeline: (id: string) => ['pipelines', id] as const,
  pipelineRunDetails: (buildId: number) => ['pipelineRunDetails', buildId] as const,
  pipelines: ['pipelines'] as const,
} as const;

// Deployment Groups Hooks
export function useDeploymentGroups() {
  return useQuery({
    queryKey: queryKeys.deploymentGroups,
    queryFn: () => apiClient.getDeploymentGroups(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useDeploymentGroup(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.deploymentGroup(id),
    queryFn: () => apiClient.getDeploymentGroup(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateDeploymentGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (group: CreateDeploymentGroupDto) => apiClient.createDeploymentGroup(group),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroups });
    },
  });
}

export function useUpdateDeploymentGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, group }: { id: string; group: UpdateDeploymentGroupDto }) => 
      apiClient.updateDeploymentGroup(id, group),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroups });
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroup(id) });
    },
  });
}

export function useDeleteDeploymentGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteDeploymentGroup(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroups });
      queryClient.removeQueries({ queryKey: queryKeys.deploymentGroup(id) });
    },
  });
}

export function useAssignPipelinesToGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: AssignPipelinesToGroupDto) => apiClient.assignPipelinesToGroup(data),
    onSuccess: (_, variables) => {
      // Invalidate deployment groups list
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroups });
      // Invalidate the specific deployment group to refresh pipelines
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroup(variables.deploymentGroupId) });
      // Invalidate pipelines cache if it exists
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

// Pipeline Hooks
export function usePipeline(id: string, limit: number = 20, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.pipeline(id),
    queryFn: () => apiClient.getPipeline(id, limit),
    enabled: enabled && !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useRefreshPipeline() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, limit }: { id: string; limit: number }) => 
      apiClient.getPipeline(id, limit),
    onSuccess: (data, { id }) => {
      // Update the cache with fresh data
      queryClient.setQueryData(queryKeys.pipeline(id), data);
    },
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiClient.deletePipeline(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.pipeline(id) });
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.deploymentGroups });
    },
  });
}

// Pipeline Run Details Hook
export function usePipelineRunDetails(buildId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.pipelineRunDetails(buildId),
    queryFn: () => apiClient.getPipelineRunDetails(buildId),
    enabled: enabled && !!buildId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Pipeline Stages Hook
export function usePipelineStages(buildId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['pipelineStages', buildId],
    queryFn: () => apiClient.getPipelineStages(buildId),
    enabled: enabled && !!buildId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Pipelines from local DB
export function usePipelines(deploymentGroupId?: string, includeDeleted?: boolean) {
  return useQuery({
    queryKey: [...queryKeys.pipelines, { deploymentGroupId, includeDeleted }],
    queryFn: () => apiClient.getPipelines(deploymentGroupId, includeDeleted),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}